import React from 'react';
import { Link } from 'react-router-dom';
import { Box, type SxProps, type Theme } from '@mui/material';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';
import { mergeSx } from 'merge-sx';

import { FolderType, OrganizationDTOStatus, OrganizationItemDto } from '@/api/generated';
import BaseCell from '@/components/DataGrid/BaseCell';
import FormattedDatetimeCell from '@/components/DataGrid/FormattedDatetimeCell';
import TextCell from '@/components/DataGrid/TextCell';
import LocationItem from '@/components/fields/Location/LocationItem';
import StatusDisplay from '@/containers/Organizations/StatusDisplay';
import { toOrganizationItems } from '@/root/linker';

const columnHelper = createColumnHelper<OrganizationItemDto>();

const getLocationItemSx = (isDraft: boolean, isInactive: boolean): SxProps<Theme> => {
  const baseStyles: SxProps<Theme> = {
    textDecoration: 'none',
    cursor: 'pointer',
    color: 'inherit'
  };

  if (isDraft) {
    return mergeSx(baseStyles, {
      '& .LocationItem-icon': {
        color: 'primary.600'
      },
      '& .LocationItem-label': {
        color: 'primary.600'
      }
    });
  }

  if (isInactive) {
    return mergeSx(baseStyles, {
      '& .LocationItem-icon': {
        color: 'grey.600'
      },
      '& .LocationItem-label': {
        color: 'grey.600'
      }
    });
  }

  return mergeSx(baseStyles, {
    '&:link': {
      color: 'inherit',
      '& .LocationItem-icon': {
        color: 'grey.800'
      },
      '& .LocationItem-label': {
        color: 'text.primary'
      }
    },
    '&:visited': {
      color: 'inherit',
      '& .LocationItem-icon': {
        color: 'grey.800'
      },
      '& .LocationItem-label': {
        color: 'text.primary'
      }
    },
    '& .LocationItem-icon': {
      color: 'grey.800'
    },
    '& .LocationItem-label': {
      color: 'text.primary'
    }
  });
};

export const organizationItemColumns = (
  t: TFunction,
  organizationSlug: string,
  handleEditDraftItem: (draftItemSlug: string, type: FolderType) => void
) =>
  [
    columnHelper.accessor('name', {
      header: () => t('layouts.organizations.items.columns.item'),
      cell: ({ getValue, row }) => {
        const isDraft = (
          [OrganizationDTOStatus.DRAFT, OrganizationDTOStatus.ONBOARDING] as OrganizationDTOStatus[]
        ).includes(row.original.status as OrganizationDTOStatus);
        const isInactive = OrganizationDTOStatus.INACTIVE === (row.original.status as OrganizationDTOStatus);

        return (
          <Box
            component={isDraft ? 'div' : Link}
            to={toOrganizationItems({
              locationSlug: row.original.slug!,
              organizationSlug
            })}
            sx={getLocationItemSx(isDraft, isInactive)}
            onClick={isDraft ? () => handleEditDraftItem(row.original.slug!, row.original.type!) : undefined}
          >
            <TextCell
              sx={{
                '& .TextCell-label': { display: 'flex', gap: 1 }
              }}
            >
              <LocationItem type={row.original.type} name={getValue()!} />
            </TextCell>
          </Box>
        );
      },
      enableSorting: false
    }),
    columnHelper.accessor('status', {
      header: () => t('layouts.organizations.items.columns.status'),
      cell: ({ getValue }) => (
        <BaseCell>
          <StatusDisplay value={getValue()!} />
        </BaseCell>
      ),
      enableSorting: false
    }),
    columnHelper.accessor('lastActivity', {
      header: () => t('layouts.organizations.items.columns.lastActivity'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue()} variant="datetime" />,
      enableSorting: false
    })
  ] as ColumnDef<OrganizationItemDto>[];
